import { IFirestoreListenerData, IFirestorePlugin, IFirestoreQueryData, IFirestoreRealtimeData } from './core/types';

import { ConsoleLoggerPlugin } from './plugins/ConsoleLoggerPlugin';
import { FirestoreSlowQueryApiLoggerPlugin } from './plugins/FirestoreSlowQueryApiLoggerPlugin';
import { FirestoreStateManager } from './plugins/FirestoreStateManager';
import { SentryFirestorePlugin } from './plugins/SentryFirestorePlugin';

export interface IFirestoreMonitorConfig {
  enableConsoleLogger?: boolean;
  enableStateManager?: boolean;
  enableApiLogger?: boolean;
  enableSentryPlugin?: boolean;
}

const DEFAULT_CONFIG: IFirestoreMonitorConfig = {
  enableConsoleLogger: false,
  enableStateManager: true,
  enableApiLogger: true,
  enableSentryPlugin: true
};

export class FirestoreMonitor {
  private static instance: FirestoreMonitor;
  private plugins: IFirestorePlugin[] = [];
  private sessionId: string;
  private startTime: number;
  private debug: boolean = false; // only for dev who is working on this tool

  private log(...args: any[]) {
    if (this.debug) {
      console.log(...args);
    }
  }

  private constructor(config: IFirestoreMonitorConfig = DEFAULT_CONFIG) {
    this.log('[DEBUG] Initializing FirestoreMonitor');
    this.sessionId = `fs-${Date.now()}`;
    this.startTime = Date.now();

    this.initializePlugins(config);
  }

  static getInstance(config?: Partial<IFirestoreMonitorConfig>): FirestoreMonitor {
    if (!FirestoreMonitor.instance) {
      FirestoreMonitor.instance = new FirestoreMonitor({ ...DEFAULT_CONFIG, ...config });
    }
    return FirestoreMonitor.instance;
  }

  private initializePlugins(config: IFirestoreMonitorConfig = DEFAULT_CONFIG): void {
    if (config.enableStateManager) {
      this.registerPlugin(new FirestoreStateManager());
    }

    if (config.enableConsoleLogger) {
      this.registerPlugin(new ConsoleLoggerPlugin());
    }

    if (config.enableApiLogger) {
      this.registerPlugin(new FirestoreSlowQueryApiLoggerPlugin({
        enabled: true,
        slowQueryThresholdMs: 1500,
        environment: process.env.NODE_ENV,
        debugSessionId: this.sessionId
      }));
    }

    if (config.enableSentryPlugin) {
      this.registerPlugin(new SentryFirestorePlugin({
        enabled: true,
        includeAllQueries: true, // Only track all queries in non-prod
        slowQueryThresholdMs: 1000
      }));
    }
  }

  trackQuery(queryData: any) {
    const startTime = Date.now();

    return (results?: any) => {
      const queryInfo = {
        ...queryData,
        duration: Date.now() - startTime,
        resultCount: results?.resultCount || 0,
        error: results?.error || null
      };

      this.plugins.forEach(plugin => {
        if (plugin.afterQuery) {
          plugin.afterQuery(queryInfo);
        }
      });

      return queryInfo;
    };
  }

  trackListener(listenerData: IFirestoreListenerData): () => void {
    this.log('[DEBUG] trackListener called with:', listenerData);
    this.afterListenerAdded(listenerData);

    return () => {
      this.afterListenerRemoved({
        ...listenerData,
        type: 'removed',
        timestamp: Date.now()
      });
    };
  }

  beforeQuery(queryData: Omit<IFirestoreQueryData, 'duration' | 'resultCount' | 'error'>): void {
    this.plugins.forEach(plugin => {
      if (plugin.enabled && plugin.beforeQuery) {
        plugin.beforeQuery(queryData);
      }
    });
  }

  afterQuery(queryData: IFirestoreQueryData): void {
    this.plugins.forEach(plugin => {
      if (plugin.enabled && plugin.afterQuery) {
        plugin.afterQuery(queryData);
      }
    });
  }

  afterListenerAdded(listenerData: IFirestoreListenerData): void {
    this.plugins.forEach(plugin => {
      if (plugin.enabled && plugin.afterListenerAdded) {
        plugin.afterListenerAdded(listenerData);
      }
    });
  }

  afterListenerRemoved(listenerData: IFirestoreListenerData): void {
    this.plugins.forEach(plugin => {
      if (plugin.enabled && plugin.afterListenerRemoved) {
        plugin.afterListenerRemoved(listenerData);
      }
    });
  }

  afterRealtimeDataReceived(realtimeData: IFirestoreRealtimeData): void {
    this.log('[DEBUG] afterRealtimeDataReceived called with:', realtimeData);
    this.plugins.forEach(plugin => {
      if (plugin.enabled && plugin.afterRealtimeDataReceived) {
        plugin.afterRealtimeDataReceived(realtimeData);
      }
    });
  }

  getPlugin<T extends IFirestorePlugin>(id: string): T | undefined {
    return this.plugins.find(plugin => plugin.id === id) as T | undefined;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getUptime(): number {
    return Date.now() - this.startTime;
  }

  public getActiveListenersCount(): number {
    const stateManager = this.getPlugin<FirestoreStateManager>('firestore-state-manager');
    return stateManager?.getActiveListenersCount() || 0;
  }

  public getAllActiveListeners(): Record<string, number> {
    const stateManager = this.getPlugin<FirestoreStateManager>('firestore-state-manager');
    return stateManager?.getAllActiveListeners() || {};
  }

  private registerPlugin(plugin: IFirestorePlugin): void {
    this.plugins.push(plugin);
  }
}

// Export a default instance for easy access
export const firestoreMonitor = FirestoreMonitor.getInstance();
